import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./clientBookingFeesSection.css";

const MIN_SPINNER_MS = 350;

export default function ClientBookingFeesSection({
    bookingId,
    className = "",
    compact = false,
    initialTotals = null, // { subtotal_amount, vat_amount, total_with_vat }
}) {
    const [fees, setFees] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [hasLoaded, setHasLoaded] = useState(false);

    const formatMoney = (val) => {
        const n = Number(val ?? 0);
        return `AED ${n.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    };

    useEffect(() => {
        if (!bookingId) return;

        const controller = new AbortController();

        const load = async () => {
            const startTs = typeof performance !== "undefined" ? performance.now() : Date.now();

            setIsLoading(true);
            setError("");
            setFees(null);
            setHasLoaded(false);

            try {
                const res = await axios.get(`/api/client-requests/bookings/${bookingId}/fees`, {
                    signal: controller.signal,
                    withCredentials: true,
                });

                const data = res?.data;

                const looksValid =
                    data &&
                    typeof data === "object" &&
                    (Array.isArray(data.price_items) ||
                        data.subtotal_amount != null ||
                        data.total_with_vat != null ||
                        data.vat_amount != null);

                if (!looksValid) {
                    setError("Your session may have expired. Please refresh and login again.");
                    setFees(null);
                    setHasLoaded(true);
                    return;
                }

                setFees(data);
                setHasLoaded(true);
            } catch (e) {
                if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;

                const status = e?.response?.status;

                if (status === 401 || status === 403) {
                    setError("Your session has expired. Please refresh and login again.");
                } else {
                    setError(e?.response?.data?.error || "Failed to load booking fees.");
                }

                setFees(null);
                setHasLoaded(true);
            } finally {
                const endTs = typeof performance !== "undefined" ? performance.now() : Date.now();
                const elapsed = endTs - startTs;
                const waitMs = Math.max(0, MIN_SPINNER_MS - elapsed);

                if (waitMs > 0) {
                    setTimeout(() => {
                        setIsLoading(false);
                    }, waitMs);
                } else {
                    setIsLoading(false);
                }
            }
        };

        load();
        return () => controller.abort();
    }, [bookingId]);

    const priceItems = useMemo(() => {
        const items = fees?.price_items;
        return Array.isArray(items) ? items : [];
    }, [fees?.price_items]);

    const subtotal = Number(fees?.subtotal_amount ?? initialTotals?.subtotal_amount ?? 0);
    const vat = Number(fees?.vat_amount ?? initialTotals?.vat_amount ?? 0);
    const totalWithVat = Number(fees?.total_with_vat ?? initialTotals?.total_with_vat ?? 0);

    const showBreakdown = priceItems.length > 0;

    const hasAnyDiscount = useMemo(
        () => priceItems.some((it) => Number(it.discount_percent ?? it.discount ?? 0) > 0),
        [priceItems]
    );

    const hasAnyTax = useMemo(
        () => priceItems.some((it) => Number(it.tax_amount ?? 0) > 0 || Number(it.tax_rate ?? 0) > 0),
        [priceItems]
    );

    const cols = hasAnyDiscount
        ? "1fr 90px 110px 110px 140px 140px 140px"
        : "1fr 90px 110px 140px 140px 140px";

    const showEmpty = hasLoaded && !isLoading && !error && !showBreakdown;

    const showOverlay = !!bookingId && !error && (!hasLoaded || isLoading);

    return (
        <section className={["client-fees", compact ? "client-fees--compact" : "", className].join(" ")}>
            {error ? <div className="client-fees__error">{error}</div> : null}

            <div className="client-fees__body">
                {showOverlay ? (
                    <div className="client-fees__loadingOverlay" aria-live="polite">
                        <div className="app-loading">
                            <div className="app-spinner" />
                            <div className="app-spinner-text">Loading Fees...</div>
                        </div>
                    </div>
                ) : null}

                {!isLoading && !error && showBreakdown ? (
                    <>
                        <div className="client-fees__tableWrap">
                            <div className="client-fees__table">
                                <div className="client-fees__head" style={{ gridTemplateColumns: cols }}>
                                    <div>Item</div>
                                    <div className="client-fees__right">Qty</div>
                                    <div className="client-fees__right">Unit Price</div>
                                    {hasAnyDiscount && <div className="client-fees__right">Discount</div>}
                                    <div className="client-fees__right">Taxable Amount</div>
                                    <div className="client-fees__right">Tax</div>
                                    <div className="client-fees__right client-fees__total">Line Total</div>
                                </div>

                                {priceItems.map((it, idx) => {
                                    const qty = Number(it.quantity || 0);
                                    // The hours before a manual adjustment. The
                                    // client already sees list-vs-charged on the
                                    // RATE; this is the same for quantity.
                                    const qtyWas = it.computed_quantity == null
                                        ? null : Number(it.computed_quantity);
                                    const unit = Number(it.unit_price || 0);
                                    const discPct = Number(it.discount_percent ?? it.discount ?? 0);

                                    const taxableAmount = Number(it.line_subtotal ?? (qty * unit));
                                    const taxAmount = Number(it.tax_amount ?? 0);
                                    const taxRate = Number(it.tax_rate ?? 0);
                                    const lineTotal = Number(it.line_total ?? 0);

                                    return (
                                        <div
                                            key={`${it.item_id || "x"}-${idx}`}
                                            className="client-fees__row"
                                            style={{ gridTemplateColumns: cols }}
                                        >
                                            <div className="client-fees__item">
                                                <div className="client-fees__itemName">{it.name || it.description || "Item"}</div>
                                                {it.description && it.name && it.description !== it.name ? (
                                                    <div className="client-fees__itemDesc">{it.description}</div>
                                                ) : null}
                                                {it.is_overridden && (
                                                    <div className="client-fees__overrideNote">
                                                        {it.override_percent != null
                                                            ? `${String(parseFloat(it.override_percent)).replace(/\.0+$/, "")}% off`
                                                            : "Special price"}
                                                        {Number(it.list_rate) > 0 && Number(it.list_rate) !== unit
                                                            ? ` — was ${formatMoney(it.list_rate)}`
                                                            : ""}
                                                        {it.override_reason ? ` · ${it.override_reason}` : ""}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="client-fees__right">
                                                {qtyWas != null && qtyWas !== qty && (
                                                    <s className="client-fees__was">{String(qtyWas).replace(/\.0+$/, "")}</s>
                                                )}
                                                {String(qty).replace(/\.0+$/, "")}
                                            </div>
                                            <div className="client-fees__right">
                                                {it.is_overridden && Number(it.list_rate) > 0 && Number(it.list_rate) !== unit && (
                                                    <s className="client-fees__was">{formatMoney(it.list_rate)}</s>
                                                )}
                                                {formatMoney(unit)}
                                            </div>

                                            {hasAnyDiscount && (
                                                <div className="client-fees__right">
                                                    {Number.isFinite(discPct) && discPct > 0
                                                        ? `${String(discPct).replace(/\.0+$/, "")}%`
                                                        : "—"}
                                                </div>
                                            )}

                                            <div className="client-fees__right">{formatMoney(taxableAmount)}</div>

                                            <div className="client-fees__right">
                                                {hasAnyTax ? (
                                                    taxRate > 0 ? (
                                                        <>
                                                            {formatMoney(taxAmount)}{" "}
                                                            <span className="client-fees__taxRate">
                                                                ({String(taxRate * 100).replace(/\.0+$/, "")}%)
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            {formatMoney(0)} <span className="client-fees__taxRate">(0%)</span>
                                                        </>
                                                    )
                                                ) : (
                                                    "—"
                                                )}
                                            </div>

                                            <div className="client-fees__right client-fees__strong client-fees__total">
                                                {formatMoney(lineTotal)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="client-fees__totals">
                            <div className="client-fees__totalRow">
                                <span>Subtotal</span>
                                <span>{formatMoney(subtotal)}</span>
                            </div>

                            <div className="client-fees__totalRow">
                                <span>VAT (5%)</span>
                                <span>{formatMoney(vat)}</span>
                            </div>

                            <div className="client-fees__totalRow client-fees__grand">
                                <strong>Total (incl. VAT)</strong>
                                <span className="client-fees__strong">{formatMoney(totalWithVat)}</span>
                            </div>
                        </div>
                    </>
                ) : null}

                {showEmpty ? <div className="client-fees__empty">No fee breakdown found for this booking yet.</div> : null}
            </div>
        </section>
    );
}